/**
 * HoldingView — MATCHING_DELAY holding experience.
 *
 * Framed as PROGRESS, never as an error. Shown to members in
 * ready_for_matching / buddy_matched / buddy_meeting_pending once the
 * server-side 72-hour threshold has been crossed.
 *
 * Reuses existing surfaces (support requests, resources). Does NOT
 * expose orbs, ecosystem, or Northstar search — those are gated behind
 * `activated`.
 */
import React from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Heart, LifeBuoy, GraduationCap, Mail, RefreshCw } from "lucide-react";
import { useActivation } from "../../hooks/useActivation";
import TalkToSomeoneNow from "../../components/TalkToSomeoneNow";

const HUMAN_STATUS = {
  ready_for_matching: "Finding your match",
  buddy_matched: "Reaching out to your match",
  buddy_meeting_pending: "Waiting on your Buddy's reply",
  registered: "Getting you started",
  intake_in_progress: "Reviewing your intake",
  intake_completed: "Finalising your intake",
  buddy_connected: "You're connected",
  activated: "You're connected",
};

const NEXT_STEP = {
  ready_for_matching: "Connect with your BTC Buddy",
  buddy_matched: "Connect with your BTC Buddy",
  buddy_meeting_pending: "Connect with your BTC Buddy",
};

export default function HoldingView() {
  const { data, loading, error, refresh } = useActivation();

  // BTC3 Phase 5 Evidence Completion · route-level guard.
  // If the server reports this patient is NOT eligible for the
  // holding experience (lifecycle_state not in {ready_for_matching,
  // buddy_matched, buddy_meeting_pending} AND no matching-delay
  // flag), redirect to the primary Dashboard so an ineligible
  // patient can never visually land here via a direct /portal/holding
  // URL.  No new UI surface is introduced.
  const HOLDING_ELIGIBLE = ["ready_for_matching", "buddy_matched", "buddy_meeting_pending"];
  if (
    data
    && !HOLDING_ELIGIBLE.includes(data.base_status)
    && !data.should_render_holding_view
  ) {
    return <Navigate to="/portal/dashboard" replace />;
  }

  return (
    <div className="max-w-3xl mx-auto py-10 px-4" data-testid="holding-view">
      <div className="rounded-3xl bg-gradient-to-br from-[#F0F7F2] via-white to-[#F0F4FA] border border-[#E4EEEA] p-8 lg:p-12 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#2D9B4E]/10 flex items-center justify-center">
            <Heart className="w-5 h-5 text-[#23803F]" />
          </div>
          <p
            className="text-xs font-bold uppercase tracking-[0.22em] text-[#23803F]"
            data-testid="holding-progress-label"
          >
            Your BTC journey is moving forward
          </p>
        </div>

        <h1
          className="text-3xl sm:text-4xl font-extrabold text-[#0B132B] tracking-tight leading-tight"
          data-testid="holding-headline"
        >
          Your Buddy match is in progress.
        </h1>
        <p className="text-[#4B5563] mt-4 text-lg leading-relaxed">
          Every match matters. Our team is making sure yours is the right one &mdash;
          this is a real, human process, not an instant one, and it&rsquo;s a
          normal part of the journey.
        </p>

        {/* BTC3 Phase 5 · D4 — Talk to Someone Now.
            Reuses the existing /api/buddy/on-call capability.  No
            fabricated Buddy identity is rendered on this view. */}
        <div className="mt-6">
          <TalkToSomeoneNow data-testid="holding-talk-to-someone-now" />
        </div>

        {loading && (
          <div className="mt-6 text-sm text-[#4B5563]" data-testid="holding-loading">
            Loading your current status&hellip;
          </div>
        )}
        {error && (
          <div className="mt-6 flex items-center gap-2 text-sm text-[#B00020]" data-testid="holding-error">
            <span>Couldn&rsquo;t load your status.</span>
            <button
              onClick={refresh}
              className="inline-flex items-center gap-1 underline"
              data-testid="holding-retry"
            >
              <RefreshCw className="w-3 h-3" /> Retry
            </button>
          </div>
        )}

        {data && (
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-gray-100 p-5" data-testid="holding-current-status">
              <p className="text-[11px] uppercase tracking-wider text-[#4B5563] font-semibold">
                Current status
              </p>
              <p className="text-xl font-bold text-[#0B132B] mt-1">
                {HUMAN_STATUS[data.status] || HUMAN_STATUS[data.base_status] || "In progress"}
              </p>
            </div>
            <div className="rounded-2xl bg-white border border-gray-100 p-5" data-testid="holding-next-step">
              <p className="text-[11px] uppercase tracking-wider text-[#4B5563] font-semibold">
                Next step
              </p>
              <p className="text-xl font-bold text-[#0B132B] mt-1">
                {NEXT_STEP[data.base_status] || "Connect with your BTC Buddy"}
              </p>
            </div>
          </div>
        )}

        <div className="mt-10 pt-8 border-t border-[#E4EEEA]">
          <h2 className="text-base font-semibold text-[#0B132B] mb-4" data-testid="holding-help-heading">
            Need help while you&rsquo;re waiting?
          </h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <Link
              to="/support"
              className="rounded-2xl bg-white border border-gray-100 p-4 hover:border-[#2D9B4E] transition-colors flex items-start gap-3"
              data-testid="holding-action-contact"
            >
              <Mail className="w-5 h-5 text-[#23803F] shrink-0" />
              <div>
                <p className="font-semibold text-[#0B132B] text-sm">Contact BTC</p>
                <p className="text-xs text-[#4B5563] mt-0.5">Talk to our team directly.</p>
              </div>
            </Link>
            <Link
              to="/portal/support-requests"
              className="rounded-2xl bg-white border border-gray-100 p-4 hover:border-[#2D9B4E] transition-colors flex items-start gap-3"
              data-testid="holding-action-support"
            >
              <LifeBuoy className="w-5 h-5 text-[#23803F] shrink-0" />
              <div>
                <p className="font-semibold text-[#0B132B] text-sm">Request Support</p>
                <p className="text-xs text-[#4B5563] mt-0.5">
                  Open a support request — we&rsquo;ll follow up.
                </p>
              </div>
            </Link>
            <Link
              to="/portal/resources"
              className="rounded-2xl bg-white border border-gray-100 p-4 hover:border-[#2D9B4E] transition-colors flex items-start gap-3"
              data-testid="holding-action-resources"
            >
              <GraduationCap className="w-5 h-5 text-[#23803F] shrink-0" />
              <div>
                <p className="font-semibold text-[#0B132B] text-sm">Explore Resources</p>
                <p className="text-xs text-[#4B5563] mt-0.5">
                  Articles and guides you can read now.
                </p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      <p className="mt-6 text-center text-xs text-[#4B5563]">
        We&rsquo;ve got you &mdash; every step of the way.
      </p>
    </div>
  );
}
